import { faBuilding, faPlus } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { router, usePage } from '@inertiajs/react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { MutedText, Paragraph } from '@/components/ui/typography';
import { resumeSignupCmsAccountsPath, switchCmsAccountsPath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import type { Account, SharedData } from '@/types/shared';

export function SwitchAccountDropdown() {
  const page = usePage<SharedData>();
  const userAccounts = (page.props.userAccounts as Account[] | undefined) ?? [];
  const pendingSignup = page.props.pendingSignup as SharedData['pendingSignup'];

  if (userAccounts.length <= 1 && !pendingSignup) {
    return null;
  }

  const handleSwitchAccount = (accountId: number) => {
    router.post(switchCmsAccountsPath(accountId));
  };

  const handleResumeSignup = () => {
    router.post(resumeSignupCmsAccountsPath());
  };

  return (
    <SidebarMenuItem>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton className="justify-start">
            <Icon icon={faBuilding} />
            <span>Switch Account</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" className="w-64">
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {userAccounts.map((account) => (
              <DropdownMenuItem
                key={account.id}
                onClick={() => handleSwitchAccount(account.id)}
                className={cn('cursor-pointer', account.active && 'bg-sidebar-accent')}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex flex-col flex-1 min-w-0">
                    <Paragraph className="font-medium truncate">{account.name}</Paragraph>
                    <MutedText className="text-xs truncate">{account.subdomain}</MutedText>
                  </div>
                  {account.active && (
                    <Badge size="sm" variant="outline">
                      Current
                    </Badge>
                  )}
                </div>
              </DropdownMenuItem>
            ))}

            {pendingSignup && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleResumeSignup} className="cursor-pointer">
                  <div className="flex items-center gap-3 w-full">
                    <Avatar>
                      <AvatarFallback className="bg-amber-100 text-amber-700">
                        <Icon icon={faPlus} />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col flex-1 min-w-0">
                      <Paragraph className="font-medium truncate">
                        {pendingSignup.accountName || 'New Firm'}
                      </Paragraph>
                      <MutedText className="text-xs truncate">Setup in progress</MutedText>
                    </div>
                    <Badge size="sm" variant="outline" className="border-amber-300 text-amber-700">
                      Pending
                    </Badge>
                  </div>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
